import { useContext, useEffect, useState } from 'react';
import { Search } from 'lucide-react';
import { toast } from 'react-toastify';
import { MyContext } from '../../context';
import DataTable from '../../components/tables/DataTable';
import Modal from '../../components/modal';
import Pagination from '../../components/Pagination';
import StatusBadge from '../../components/StatusBadge';
import SimpleDropdown from '../../components/SimpleDropdown';
import Switch from '../../components/Switch';
import {
	listStoreUsers,
	updateStoreUserRole,
	updateStoreUserApprover,
	revokeStoreUserAccess,
	inviteStoreUser,
	resendStoreUserInvite,
} from '../../utils/queries';

const PAGE_SIZE = 20;
// PLATFORMADMIN is deliberately never offered here -- assigning it stays
// exclusive to the unrestricted admin/users page (usersAdmin.routes.ts).
const ROLES = ['ADMIN', 'USER', 'VIEWER'];
const ROLE_DESCRIPTIONS = {
	ADMIN: 'Full access to this store, including managing its team and settings.',
	USER: 'Can manage day-to-day Control Center (orders, products, etc.) but not the team.',
	VIEWER: 'Read-only access - can view store data but cannot make changes.',
};
const ROLE_OPTIONS = ROLES.map((r) => ({ value: r, label: r, description: ROLE_DESCRIPTIONS[r] }));
const emptyForm = { email: '', firstname: '', lastname: '', role: 'USER' };

export default function StoreUsersList() {
	const { activeStoreId } = useContext(MyContext);
	const [users, setUsers] = useState([]);
	const [total, setTotal] = useState(0);
	const [page, setPage] = useState(0);
	const [search, setSearch] = useState('');
	const [loading, setLoading] = useState(true);

	const [open, setOpen] = useState(false);
	const [form, setForm] = useState(emptyForm);
	const [saving, setSaving] = useState(false);
	const [error, setError] = useState('');
	const [resultMessage, setResultMessage] = useState(null);

	const refresh = () => {
		setLoading(true);
		listStoreUsers({
			limit: PAGE_SIZE,
			offset: page * PAGE_SIZE,
			search: search || undefined,
			storeId: activeStoreId || undefined,
		}).then((result) => {
			if (result?.success) {
				setUsers(result.data.users);
				setTotal(result.data.pagination.total);
			}
			setLoading(false);
		});
	};

	useEffect(() => {
		refresh();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [page, search, activeStoreId]);

	const openCreate = () => {
		setForm(emptyForm);
		setError('');
		setResultMessage(null);
		setOpen(true);
	};

	const handleSubmit = async (e) => {
		e.preventDefault();
		setSaving(true);
		setError('');
		setResultMessage(null);
		const result = await inviteStoreUser({ ...form, storeId: activeStoreId });
		setSaving(false);
		if (!result?.success) {
			setError(result?.error?.message || 'Failed to send invite.');
			return;
		}
		if (result.data?.granted) {
			setResultMessage({
				granted: true,
				text: 'This person already has an account - access to this store was granted and they were notified by email.',
			});
		} else {
			setResultMessage({ granted: false, text: 'Invite sent.' });
		}
		refresh();
	};

	const handleRoleChange = async (userId, role) => {
		await updateStoreUserRole(userId, activeStoreId, role);
		refresh();
	};

	const handleApproverChange = async (row, isOrderApprover) => {
		const result = await updateStoreUserApprover(row.userId, activeStoreId, isOrderApprover, row.role);
		if (!result?.success) {
			toast.error(result?.error?.message || 'Failed to update Order Approver.');
			return;
		}
		refresh();
	};

	const handleRevoke = async (accessId) => {
		if (!accessId) return;
		const result = await revokeStoreUserAccess(accessId);
		if (!result?.success) {
			toast.error(result?.error?.message || 'Failed to remove access.');
			return;
		}
		toast.success('Access removed.');
		refresh();
	};

	const [resendingUserId, setResendingUserId] = useState(null);
	const handleResendInvite = async (userId) => {
		setResendingUserId(userId);
		const result = await resendStoreUserInvite(userId, activeStoreId);
		setResendingUserId(null);
		if (!result?.success) {
			toast.error(result?.error?.message || 'Failed to resend invite.');
			return;
		}
		toast.success('Invite resent.');
	};

	const columns = [
		{ key: 'email', header: 'Email' },
		{ key: 'firstname', header: 'First Name' },
		{ key: 'lastname', header: 'Last Name' },
		{
			key: 'role',
			header: 'Role',
			render: (value, row) => (
				<SimpleDropdown
					value={value}
					onChange={(v) => handleRoleChange(row.userId, v)}
					options={ROLES.map((r) => ({ value: r, label: r }))}
					className='w-32'
					disabled={!row.activeFlag}
				/>
			),
		},
		{
			key: 'isOrderApprover',
			header: 'Order Approver',
			// NOTE: this reads/writes user_store_access.is_order_approver, but
			// listStoreUsers doesn't select that attribute yet and the PATCH
			// endpoint doesn't persist it either (see queries.js's
			// updateStoreUserApprover) -- until the backend catches up this
			// always renders off and toggling it is a no-op.
			render: (value, row) => (
				<Switch
					checked={!!value}
					disabled={!row.activeFlag}
					onChange={(checked) => handleApproverChange(row, checked)}
				/>
			),
		},
		{
			key: 'activeFlag',
			header: 'Status',
			render: (value) => <StatusBadge status={value ? 'active' : 'pending'} />,
		},
		{
			key: 'userStoreAccessId',
			header: '',
			sortable: false,
			render: (accessId, row) => (
				<div className='flex gap-2 justify-end'>
					{!row.activeFlag && (
						<button
							type='button'
							className='btn-secondary-small'
							disabled={resendingUserId === row.userId}
							onClick={() => handleResendInvite(row.userId)}
						>
							{resendingUserId === row.userId ? 'Resending...' : 'Resend Invite'}
						</button>
					)}
					{accessId && (
						<button type='button' className='btn-secondary-small' onClick={() => handleRevoke(accessId)}>
							Revoke
						</button>
					)}
				</div>
			),
		},
	];

	return (
		<div className='py-8 pr-8'>
			<div className='flex items-center justify-between mb-6'>
				<h1 className='text-2xl font-light tracking-tight'>Team</h1>
				<button type='button' className='btn-primary' onClick={openCreate}>
					New Team Member
				</button>
			</div>

			<div className='mb-5'>
				<label>Search</label>
				<div className='relative max-w-sm'>
					<Search className='pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 size-4 text-gray-400' />
					<input
						className='w-52 form-input pl-9'
						value={search}
						onChange={(e) => {
							setSearch(e.target.value);
							setPage(0);
						}}
					/>
				</div>
			</div>

			<DataTable columns={columns} data={users} loading={loading} emptyHeading='No team members yet' />
			<Pagination pageCount={Math.ceil(total / PAGE_SIZE)} currentPage={page} onPageChange={setPage} />

			<Modal open={open} setOpen={setOpen} title='New Team Member'>
				<form onSubmit={handleSubmit} className='space-y-4'>
					{error && <div className='text-sm text-pi-red'>{error}</div>}
					{resultMessage && resultMessage.granted && (
						<div className='rounded-md border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800'>
							{resultMessage.text}
						</div>
					)}
					{resultMessage && !resultMessage.granted && (
						<div className='text-sm text-green-700'>{resultMessage.text}</div>
					)}
					<div>
						<label>Email</label>
						<input
							type='email'
							className='form-input'
							required
							value={form.email}
							onChange={(e) => setForm({ ...form, email: e.target.value })}
						/>
					</div>
					<div className='w-1/2'>
						<label>First Name</label>
						<input
							className='form-input'
							value={form.firstname}
							onChange={(e) => setForm({ ...form, firstname: e.target.value })}
						/>
					</div>
					<div className='w-1/2'>
						<label>Last Name</label>
						<input
							className='form-input'
							value={form.lastname}
							onChange={(e) => setForm({ ...form, lastname: e.target.value })}
						/>
					</div>
					<div>
						<label>Role</label>
						<SimpleDropdown
							value={form.role}
							onChange={(v) => setForm({ ...form, role: v })}
							options={ROLE_OPTIONS}
						/>
					</div>
					<p className='text-xs text-gray-500'>
						An email invite will be sent so this person can choose their own password.
					</p>
					<div className='flex gap-3 pt-2'>
						<button type='submit' disabled={saving} className='btn-primary'>
							{saving ? 'Sending...' : 'Send Invite'}
						</button>
						<button type='button' className='btn-secondary' onClick={() => setOpen(false)}>
							Close
						</button>
					</div>
				</form>
			</Modal>
		</div>
	);
}
